import { BaseFlexSeoResponse } from "./BaseFlexSeoResponse";
import { Composition, ViewModelResponse } from "typings/flexFramework/FlexDefinitions";
import { findFlexComponent } from "src/stores/CompositionStore/CompositionStore";
import { PageStore } from "bernie-core";
import { NamedMeta, PaginationAttribute, PropertyMeta } from "bernie-http";

/**
 * Implementation of a BaseFlexSeo that represents a ViewModelResponse's SEO Metadata
 * with a composition.
 */
export class FlexSeoWithCompositionResponse extends BaseFlexSeoResponse implements ViewModelResponse {
  public composition: Composition;
  public hrefNextPage: string;
  public hrefPreviousPage: string;

  public constructor(viewModelResponse: ViewModelResponse) {
    super(viewModelResponse);
    this.composition = viewModelResponse.composition;
    this.hrefNextPage = viewModelResponse.hrefNextPage;
    this.hrefPreviousPage = viewModelResponse.hrefPreviousPage;
  }

  public hasEnoughData = () => this.composition && super.hasEnoughData();
  public getTemplateId = () =>
    `${this.flexContext.siteContext.id}-${this.getResolvedLocale()}-${this.composition.templateName}`;
  public getDataFmSeo = () => this.composition.meta["data-fm-seo"];
  public getDescription = () => this.composition.meta["description"];
  public getKeywords = () => this.composition.meta["keywords"];
  public getOgImage = () => this.getHeroImageUrl();
  public getTemplateFmId = () => this.composition.templateId;
  public getTemplateVersion = () => this.composition.templateVersion;
  public getTitle = () => this.composition.title;
  public getRobots = () => this.composition.meta["robots"];

  /**
   * Dives in composition to find the hero image url.
   */
  private getHeroImageUrl() {
    const wizardRegion = findFlexComponent(this.composition.page.regionList, (c) => c.name === "wizard");
    const destinationWizard = wizardRegion?.modules?.find((module) => module.name === "DestinationWizard");
    const wizardHero = destinationWizard?.modules?.find((module) => module.name === "destination-hero");
    const mediaUrl = wizardHero?.model?.content?.media[0]?.mediaUrl;

    return mediaUrl && (mediaUrl.startsWith("http") ? mediaUrl : `https:${mediaUrl}`);
  }

  // Override SEO meta data when in template rendering path.
  public overridePageStore(pageStore: PageStore) {
    if (!this.composition) {
      return;
    }

    pageStore.title = this.getTitle() || pageStore.title;
    pageStore.seo = {
      ...pageStore.seo,
      description: this.getDescription() || pageStore.seo.description,
      additionalMeta: pageStore.seo.additionalMeta,
    };

    if (this.getKeywords()) {
      pageStore.seo?.additionalMeta?.push(new NamedMeta("keywords", this.getKeywords()));
    }
    if (this.getDataFmSeo()) {
      pageStore.seo?.additionalMeta?.push(new NamedMeta("data-fm-seo", this.getDataFmSeo()));
    }
    if (this.getOgImage()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:image", this.getDataFmSeo()));
    }
    if (this.getDescription()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:description", this.getDescription()));
    }
    if (this.getTitle()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:title", this.getTitle()));
    }
    if (this.getOgImage()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:image", this.getDataFmSeo()));
    }
    if (this.hrefNextPage) {
      pageStore.seo?.additionalMeta?.push(new PaginationAttribute("next", this.hrefNextPage));
    }
    if (this.hrefPreviousPage) {
      pageStore.seo?.additionalMeta?.push(new PaginationAttribute("prev", this.hrefPreviousPage));
    }
  }
}
